import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { cx } from './cx';
import { Icon } from './Icon';

/**
 * A box to drop a file on, or to click and choose one — in the console's own words.
 *
 * The browser's own file input is a different control on every browser, and it speaks the
 * browser's language rather than the firm's: a Bulgarian administrator met "Choose File No
 * file chosen" on the places import and could not tell where to press. So the input is hidden
 * and this is what a person sees: the whole box is the button, a file dropped on it is taken
 * the same as one chosen, and once there is one its name and size stand where the prompt was.
 *
 * `reset` changes when the caller starts over, so the same file can be chosen again — an
 * input keeps its value, and choosing what it already holds fires no change.
 */
export function FileDrop({
  accept,
  hint,
  file,
  disabled = false,
  reset,
  onFile,
}: {
  /** As the input's `accept`: `.csv,text/csv`. Dropping is not filtered by it; the reader says. */
  accept?: string;
  /** What the file should be — "CSV, as Export CSV gives it". */
  hint?: string;
  /** The file taken, to show its name; null before there is one. */
  file: { name: string; size: number } | null;
  disabled?: boolean;
  reset?: number | string;
  onFile: (file: File) => void;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function take(files: FileList | null | undefined) {
    const first = files?.[0];

    if (first !== undefined && !disabled) onFile(first);
  }

  function dropped(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setOver(false);
    take(event.dataTransfer.files);
  }

  return (
    <>
      <input
        key={reset}
        ref={input}
        type="file"
        accept={accept}
        className="hidden"
        tabIndex={-1}
        aria-hidden
        onChange={(event) => take(event.target.files)}
      />

      <button
        type="button"
        disabled={disabled}
        onClick={() => input.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={dropped}
        className={cx(
          'w-full flex flex-col items-center justify-center gap-1.5 px-4 py-6 rounded-card',
          'border-2 border-dashed text-center transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
          over ? 'border-brand bg-brand-soft' : 'border-rule bg-surface-2 hover:border-ink-3',
          disabled ? 'opacity-60 cursor-default' : 'cursor-pointer',
        )}
      >
        <span className={cx('flex items-center justify-center w-9 h-9 rounded-full', over ? 'text-brand' : 'text-ink-2')}>
          <Icon name="upload" />
        </span>

        {file === null ? (
          <>
            <span className="text-body font-semibold text-ink">
              {over ? t('fileDrop.release') : t('fileDrop.drop')}
            </span>
            <span className="text-secondary text-ink-2">
              {t('fileDrop.or')} <span className="text-brand font-semibold underline">{t('fileDrop.choose')}</span>
            </span>
          </>
        ) : (
          <>
            <span className="text-body font-semibold text-ink break-all">{file.name}</span>
            <span className="text-secondary text-ink-2">
              {t('fileDrop.size', { size: Math.max(1, Math.round(file.size / 1024)).toLocaleString() })} ·{' '}
              <span className="text-brand font-semibold underline">{t('fileDrop.another')}</span>
            </span>
          </>
        )}

        {hint !== undefined && <span className="text-secondary text-ink-3">{hint}</span>}
      </button>
    </>
  );
}
